import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaLock, FaEnvelope, FaFacebookF, FaGoogle, FaLinkedinIn } from 'react-icons/fa';
import { HiSelector } from 'react-icons/hi';
import { useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

const LoginPage = () => {
    const [userType, setUserType] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userType) {
          toast.error('Please select a role');
          return;
        }
        try {
          const success = await login(email, password, userType);
          if (success) {
            toast.success('Login successful!');
            // Set a flag in localStorage to indicate that we need to reload
            localStorage.setItem('needsReload', 'true');
            // Navigate based on user type
            let route;
            switch(userType) {
              case 'alumni':
                route = '/dashboard';
                break;
              case 'institute':
                route = '/instituteDashboard';
                break;
              case 'employer':
                route = '/employerDashboard';
                break;
              case 'student':
                route = '/studentDashboard';
                break;
              case 'faculty':
                route = '/facultyDashboard';
              case 'admin':
                route = '/admin';
                break;
              case 'admin':
                route = '/admin';
              default:
                route = '/dashboard';
            }
            // Use window.location for navigation to force a full page reload
            window.location.href = route;
          } else {
            toast.error('Invalid email or password');
          }
        } catch (error) {
          toast.error(error.message || 'Login failed');
        }
    };

    const userTypes = [
        { id: 'alumni', label: 'Alumni' },
        { id: 'institute', label: 'Institute' },
        { id: 'employer', label: 'Employer' },
        { id: 'student', label: 'Student' },
        { id: 'faculty', label: 'Faculty' },
        { id: 'admin', label: 'Admin' },
    ];

    return (
        <div className="flex h-screen bg-gray-100">
            <Toaster position="top-center" reverseOrder={false} />

            {/* Left side with image */}
            <div className="hidden lg:flex lg:w-1/2 bg-cover bg-center" style={{backgroundImage: "url('https://img.freepik.com/premium-vector/college-university-students-campus-community-vector-illustration_497046-2521.jpg?w=740')"}}>
                <div className="w-full flex flex-col justify-center items-center bg-black bg-opacity-50 text-white p-12">
                    <h1 className="text-4xl font-bold mb-4">Welcome Back!</h1>
                    <p className="text-xl text-center">Connect with your alumni network and unlock new opportunities.</p>
                </div>
            </div>

            {/* Right side with login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="user-type" className="block text-sm font-medium text-gray-700">Select Role</label>
                            <div className="mt-1 relative">
                                <select
                                    id="user-type"
                                    value={userType}
                                    onChange={(e) => setUserType(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value="">Select Role</option>
                                    {userTypes.map((type) => (
                                        <option key={type.id} value={type.id}>{type.label}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <HiSelector className="h-4 w-4" aria-hidden="true" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                            <div className="mt-1 relative">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                            <div className="mt-1 relative">
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Enter your password"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500">
                                    Forgot your password?
                                </a>
                            </div>
                        </div>

                        <div>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Sign in
                            </motion.button>
                        </div>
                    </form>

                    {/* Social login options */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or continue with</span>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-3 gap-3">
                            <div>
                                <a href="#" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                                    <FaFacebookF className="h-5 w-5" />
                                </a>
                            </div>
                            <div>
                                <a href="#" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                                    <FaGoogle className="h-5 w-5" />
                                </a>
                            </div>
                            <div>
                                <a href="#" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                                    <FaLinkedinIn className="h-5 w-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;